import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { isAxiosError } from 'axios';
import { getJobStatus } from '@/services/evaluationApi';

/**
 * PS-0a (score-fast split) — terminal vs non-terminal statuses.
 *
 * `scored` is intentionally NOT in TERMINAL_STATUSES: scoring is done but
 * LLM coaching is still generating. Polling must continue until `completed`
 * or `failed` so the parent can pick up the coaching narrative.
 *
 * SCORE_READY_STATUSES: statuses where result_evaluation_id is populated and
 * the numeric score is fetchable. Used to expose `scoreReady` so the call
 * site can navigate to results immediately without waiting for coaching.
 */
const TERMINAL_STATUSES = new Set(['completed', 'failed']);
const SCORE_READY_STATUSES = new Set(['scored', 'completed']);

/**
 * F7 — Tiered polling intervals calibrated to typical evaluation completion windows.
 * Elapsed time is derived from job.submitted_at (server clock) so a browser
 * refresh does not reset the timer to 0.
 */
export function getPollingInterval(elapsedMs: number): number {
  if (elapsedMs < 10_000) return 1_000;    // 0-10 s: every 1 s
  if (elapsedMs < 45_000) return 3_000;    // 10-45 s: every 3 s
  if (elapsedMs < 120_000) return 5_000;   // 45-120 s: every 5 s
  return 10_000;                            // 120 s+: every 10 s
}

/**
 * Poll a single evaluation job until it reaches a terminal state, then
 * invalidate list caches.
 *
 * Navigation on score-ready is intentionally NOT done here — the call site
 * must handle navigation in its own useEffect (Frontend #18). This keeps
 * the hook a pure data layer and prevents double-navigate when the hook is
 * mounted in multiple places simultaneously.
 *
 * Returns the standard React Query result plus convenience fields:
 * - `isCompleted`   — true when status === 'completed' (LLM coaching ready)
 * - `scoreReady`    — true when status === 'scored' OR 'completed' (numeric
 *                     score available, result_evaluation_id populated). Call
 *                     sites should navigate / reveal results at this point.
 * - `resultEvaluationId` — the evaluation UUID as soon as it is present
 *                          (populated at 'scored', not just 'completed').
 *
 * - jobId: the job_id returned by submitEvaluation(). Pass null to disable.
 * - Implements F2 (401 stops retries), F3 (iOS Safari backgrounding recovery),
 *   F6 (cache invalidation on score-ready), F7 (tiered intervals).
 */
export function useEvaluationJob(jobId: string | null) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['evaluation-job', jobId],
    queryFn: () => getJobStatus(jobId!),
    enabled: !!jobId,
    // Refetch when the tab regains focus (F3 partial: programmatic refetch on
    // visibilitychange is added via the effect below; this handles regular focus).
    refetchOnWindowFocus: true,
    // F2 — do not retry 401s; they indicate an expired session, not a transient error.
    retry: (failureCount, error) =>
      isAxiosError(error) && error.response?.status !== 401 && failureCount < 2,
    // F7 — tiered polling; stop only when the job reaches a terminal state.
    // `scored` is NOT terminal — polling must continue to pick up `completed`.
    refetchInterval: (q) => {
      const data = q.state.data;
      if (!data || TERMINAL_STATUSES.has(data.status)) return false;
      const elapsed = Date.now() - new Date(data.submitted_at).getTime();
      return getPollingInterval(elapsed);
    },
  });

  // F3 — iOS Safari freezes JS timers when backgrounded. Manually refetch on
  // visibilitychange so the user sees current state immediately on foregrounding.
  // Destructure refetch (stable reference) so the effect doesn't re-run on
  // every query state change — previously listed query.refetch but depended on
  // the whole query object, leaking listeners with every poll tick.
  const { refetch } = query;
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible' && jobId) {
        void refetch();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [jobId, refetch]);

  // F6 — PS-0a: When the score is ready (status === 'scored' OR 'completed'),
  // invalidate the evaluation list caches so the new row is visible immediately
  // in /evaluations. We fire at score-ready (not just completed) because the
  // result_evaluation_id is populated and the eval row exists in the DB at that
  // point. Navigation is the call site's responsibility (see Frontend #18 /
  // B-2026-0024).
  useEffect(() => {
    const status = query.data?.status;
    const evalId = query.data?.result_evaluation_id;
    if (evalId && (status === 'scored' || status === 'completed')) {
      queryClient.invalidateQueries({ queryKey: ['evaluations'] });
      queryClient.invalidateQueries({ queryKey: ['my-evaluations'] });
      queryClient.invalidateQueries({ queryKey: ['coach-athlete-eval-history'] });
    }
  }, [query.data?.status, query.data?.result_evaluation_id, queryClient]);

  const isCompleted = query.data?.status === 'completed';

  // scoreReady: score + all numeric/visual results are present and fetchable.
  // True as soon as status is 'scored' OR 'completed' (coaching may still be
  // null at 'scored' — EvaluationResults polls for it separately).
  const scoreReady = query.data?.status != null && SCORE_READY_STATUSES.has(query.data.status);

  // resultEvaluationId is exposed as soon as it is present — at 'scored',
  // NOT just 'completed'. This is what allows the call site to navigate early.
  const resultEvaluationId: string | null =
    scoreReady && query.data?.result_evaluation_id
      ? query.data.result_evaluation_id
      : null;

  return {
    ...query,
    isCompleted,
    scoreReady,
    resultEvaluationId,
  };
}
